import React from 'react';

/**
 * Flat navigation/UI icons from the kid-first redesign (docs/design/kid-redesign/icons/*.svg,
 * imported without their embedded metadata). Inline SVG so the outline follows light/dark mode
 * via the --kid-ink CSS variable (src/index.css). One object, one dominant colour per icon.
 */
export type KidIconName = 'home' | 'tasks' | 'calendar' | 'rewards' | 'stats' | 'settings' | 'lock' | 'star';

const INK = 'var(--kid-ink)';
const PAPER = 'var(--kid-icon-paper)';

const PATHS: Record<KidIconName, React.ReactNode> = {
  home: (
    <>
      <path d="M4 11 12 4l8 7v9H4z" fill="#E8833A" stroke={INK} strokeWidth="2" strokeLinejoin="round" />
      <path d="M10 20v-5h4v5" fill={PAPER} stroke={INK} strokeWidth="2" strokeLinejoin="round" />
    </>
  ),
  tasks: (
    <>
      <rect x="5" y="3.5" width="14" height="17" rx="2.5" fill="#6FCF97" stroke={INK} strokeWidth="2" />
      <path d="m8.5 12.5 2.5 2.5 4.5-5" fill="none" stroke={INK} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  calendar: (
    <>
      <rect x="4" y="5.5" width="16" height="15" rx="2.5" fill="#7FB3F5" stroke={INK} strokeWidth="2" />
      <path d="M4 10.5h16M8.5 3.5v4M15.5 3.5v4" fill="none" stroke={INK} strokeWidth="2" strokeLinecap="round" />
    </>
  ),
  rewards: (
    <>
      <path d="M7 4h10v5a5 5 0 0 1-10 0z" fill="#F5B93A" stroke={INK} strokeWidth="2" strokeLinejoin="round" />
      <path d="M7 6H4.5a2.6 2.6 0 0 0 2.7 3.4M17 6h2.5a2.6 2.6 0 0 1-2.7 3.4M12 14v4.5M8.5 20h7" fill="none" stroke={INK} strokeWidth="2" strokeLinecap="round" />
    </>
  ),
  stats: (
    <>
      <rect x="3.5" y="12" width="4.5" height="8.5" rx="1" fill="#B79AE0" stroke={INK} strokeWidth="2" />
      <rect x="9.75" y="7.5" width="4.5" height="13" rx="1" fill="#B79AE0" stroke={INK} strokeWidth="2" />
      <rect x="16" y="3.5" width="4.5" height="17" rx="1" fill="#B79AE0" stroke={INK} strokeWidth="2" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="7.5" fill="none" stroke={INK} strokeWidth="4" strokeDasharray="3 2.89" />
      <circle cx="12" cy="12" r="6" fill="#B0BEC5" stroke={INK} strokeWidth="2" />
      <circle cx="12" cy="12" r="2" fill={PAPER} stroke={INK} strokeWidth="2" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10.5" width="14" height="10" rx="2.5" fill="#F5B93A" stroke={INK} strokeWidth="2" />
      <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" fill="none" stroke={INK} strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="15.5" r="1.4" fill={INK} />
    </>
  ),
  star: (
    <path d="m12 2.8 2.8 5.8 6.3.8-4.6 4.4 1.2 6.3L12 17l-5.7 3.1 1.2-6.3L2.9 9.4l6.3-.8z" fill="#F5B93A" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" />
  ),
};

export const KidIcon: React.FC<{ name: KidIconName; size?: number; className?: string; title?: string }> = ({
  name, size = 24, className, title,
}) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    className={className}
    role={title ? 'img' : undefined}
    aria-hidden={title ? undefined : true}
    aria-label={title}
    focusable="false"
  >
    {PATHS[name]}
  </svg>
);

/** The one star used for every star in the app (design rule: "One star"). */
export const Star: React.FC<{ size?: number; className?: string; title?: string }> = (props) => (
  <KidIcon name="star" {...props} />
);

export default KidIcon;
